import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { Plus, CheckCircle, XCircle, ArrowClockwise, Eye, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { DocumentUploadSection } from '../components/DocumentUploadSection';

export default function InspectionPage() {
  const [inspections, setInspections] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    loadInspections();
  }, []);

  const loadInspections = async () => {
    try {
      const response = await api.get('/inspections');
      setInspections(response.data);
    } catch (error) {
      toast.error('Failed to load inspections');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    try {
      await api.post('/inspections', {
        coating_type: formData.get('coating_type'),
        batch_number: formData.get('batch_number'),
        part_number: formData.get('part_number'),
        thickness: parseFloat(formData.get('thickness')),
        adhesion: formData.get('adhesion'),
        color: formData.get('color'),
        surface_finish: formData.get('surface_finish'),
        chemical_composition: formData.get('chemical_composition'),
        result: formData.get('result'),
        comments: formData.get('comments'),
      });
      toast.success('Inspection record created');
      setShowCreateModal(false);
      loadInspections();
      e.target.reset();
    } catch (error) {
      toast.error('Failed to create inspection');
    }
  };

  const handleDeleteInspection = async (inspectionId) => {
    if (!window.confirm('Are you sure you want to delete this inspection? This action cannot be undone.')) {
      return;
    }
    try {
      await api.delete(`/inspections/${inspectionId}`);
      toast.success('Inspection deleted successfully');
      loadInspections();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete inspection');
    }
  };

  const getResultIcon = (result) => {
    switch (result) {
      case 'pass': return <CheckCircle size={20} weight="fill" className="text-green-600" />;
      case 'fail': return <XCircle size={20} weight="fill" className="text-red-600" />;
      case 'rework': return <ArrowClockwise size={20} weight="fill" className="text-purple-600" />;
      default: return null;
    }
  };

  const getCoatingLabel = (type) => {
    const labels = {
      'anodizing': 'Anodizing',
      'ptfe': 'PTFE',
      'zinc_aluminium': 'Zinc Aluminium Flake'
    };
    return labels[type] || type;
  };

  return (
    <div data-testid="inspection-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">Quality Inspections</h1>
          <p className="mt-1 text-sm text-gray-600">Coating quality inspection records</p>
        </div>
        <button
          data-testid="create-inspection-button"
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 bg-gray-900 text-white px-4 py-2 rounded-sm hover:bg-gray-800 transition-colors"
        >
          <Plus size={20} weight="bold" />
          New Inspection
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading inspections...</div>
      ) : inspections.length === 0 ? (
        <div className="text-center py-12 bg-white border border-gray-200 rounded-sm">
          <p className="text-gray-500">No inspections yet. Create one to get started.</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-sm overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Coating Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Batch</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Part</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Thickness (μm)</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Adhesion</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Surface</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Result</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {inspections.map((insp, idx) => (
                <tr key={insp.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} data-testid={`inspection-row-${idx}`}>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{getCoatingLabel(insp.coating_type)}</td>
                  <td className="px-4 py-3 text-sm text-gray-600 mono">{insp.batch_number}</td>
                  <td className="px-4 py-3 text-sm text-gray-600 mono">{insp.part_number}</td>
                  <td className="px-4 py-3 text-sm text-gray-600 mono">{insp.thickness}</td>
                  <td className="px-4 py-3 text-sm text-gray-600 capitalize">{insp.adhesion}</td>
                  <td className="px-4 py-3 text-sm text-gray-600 capitalize">{insp.surface_finish}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {getResultIcon(insp.result)}
                      <span className={`px-2 py-1 text-xs font-medium rounded-sm ${
                        insp.result === 'pass' ? 'bg-green-100 text-green-700' :
                        insp.result === 'fail' ? 'bg-red-100 text-red-700' :
                        'bg-purple-100 text-purple-700'
                      }`}>
                        {insp.result.toUpperCase()}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{new Date(insp.inspection_date).toLocaleDateString()}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button
                        data-testid={`view-inspection-${idx}`}
                        onClick={() => setSelectedInspection(insp)}
                        className="flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm font-medium"
                      >
                        <Eye size={16} /> Documents
                      </button>
                      {(user?.role === 'admin' || user?.role === 'qa_manager') && (
                        <button
                          data-testid={`delete-inspection-${idx}`}
                          onClick={() => handleDeleteInspection(insp.id)}
                          className="flex items-center gap-1 text-red-600 hover:text-red-700 text-sm font-medium"
                        >
                          <Trash size={16} /> Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-sm w-full max-w-2xl my-8">
            <div className="border-b border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900">New Quality Inspection</h2>
            </div>
            <form onSubmit={handleCreate} className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">COATING TYPE</label>
                  <select
                    name="coating_type"
                    data-testid="inspection-coating-type"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  >
                    <option value="anodizing">Anodizing</option>
                    <option value="ptfe">PTFE</option>
                    <option value="zinc_aluminium">Zinc Aluminium Flake</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">BATCH NUMBER</label>
                  <input
                    name="batch_number"
                    data-testid="inspection-batch-number"
                    type="text"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">PART NUMBER</label>
                  <input
                    name="part_number"
                    data-testid="inspection-part-number"
                    type="text"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">THICKNESS (μm)</label>
                  <input
                    name="thickness"
                    data-testid="inspection-thickness"
                    type="number"
                    step="0.01"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent mono"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">ADHESION</label>
                  <select
                    name="adhesion"
                    data-testid="inspection-adhesion"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  >
                    <option value="excellent">Excellent</option>
                    <option value="good">Good</option>
                    <option value="fair">Fair</option>
                    <option value="poor">Poor</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">COLOR</label>
                  <input
                    name="color"
                    data-testid="inspection-color"
                    type="text"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">SURFACE FINISH</label>
                  <select
                    name="surface_finish"
                    data-testid="inspection-surface-finish"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  >
                    <option value="smooth">Smooth</option>
                    <option value="rough">Rough</option>
                    <option value="uneven">Uneven</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">CHEMICAL COMPOSITION</label>
                  <input
                    name="chemical_composition"
                    data-testid="inspection-chemical"
                    type="text"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">RESULT</label>
                  <select
                    name="result"
                    data-testid="inspection-result"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  >
                    <option value="pass">Pass</option>
                    <option value="fail">Fail</option>
                    <option value="rework">Rework</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-900 mb-2">COMMENTS</label>
                  <textarea
                    name="comments"
                    data-testid="inspection-comments"
                    rows={2}
                    className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  />
                </div>
              </div>
              <div className="flex gap-3 pt-6">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-sm hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="inspection-submit"
                  className="flex-1 px-4 py-2 bg-gray-900 text-white rounded-sm hover:bg-gray-800 transition-colors"
                >
                  Create Inspection
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {selectedInspection && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-sm w-full max-w-3xl my-8">
            <div className="border-b border-gray-200 p-6 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Inspection Evidence Documents</h2>
                <p className="text-sm text-gray-600 mt-1">
                  {getCoatingLabel(selectedInspection.coating_type)} • Batch: {selectedInspection.batch_number}
                </p>
              </div>
              <button
                onClick={() => setSelectedInspection(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <div className="p-6">
              <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-sm">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Part Number</p>
                    <p className="font-medium text-gray-900 mono">{selectedInspection.part_number}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Thickness</p>
                    <p className="font-medium text-gray-900 mono">{selectedInspection.thickness} μm</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Adhesion</p>
                    <p className="font-medium text-gray-900 capitalize">{selectedInspection.adhesion}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Result</p>
                    <span className={`inline-block px-2 py-1 text-xs font-medium rounded-sm ${
                      selectedInspection.result === 'pass' ? 'bg-green-100 text-green-700' :
                      selectedInspection.result === 'fail' ? 'bg-red-100 text-red-700' :
                      'bg-purple-100 text-purple-700'
                    }`}>
                      {selectedInspection.result.toUpperCase()}
                    </span>
                  </div>
                </div>
              </div>
              <DocumentUploadSection 
                category="inspection"
                relatedId={selectedInspection.id}
                title="Quality Evidence Documents"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
